import pptxgen from 'pptxgenjs';
import { safeSaveAs } from './safariDownload';
import type {
  PresentationJSON,
  SlideJSON,
  SlideElement,
  TextElement,
  TextRun,
  BulletElement,
  NumberedElement,
  BulletItem,
  ImageElement,
  ShapeElement,
  TableElement,
  PresentationTableCell,
  ChartElement,
  ShadowOptions,
} from '../types/presentation';

// ============================================================================
// Color Helpers
// ============================================================================

/** Strip # prefix from hex colors — PptxGenJS corrupts files with # */
function cleanColor(color: string | undefined): string | undefined {
  if (!color) return undefined;
  return color.replace(/^#/, '');
}

/** Build shadow options (always fresh object to avoid PptxGenJS mutation) */
function buildShadow(shadow: ShadowOptions): pptxgen.ShadowProps {
  return {
    type: shadow.type,
    color: cleanColor(shadow.color) || '000000',
    blur: shadow.blur ?? 6,
    offset: shadow.offset ?? 2,
    angle: shadow.angle ?? 135,
    opacity: shadow.opacity ?? 0.15,
  };
}

// ============================================================================
// Shape Type Mapping (PptxGenJS v4 uses SHAPE_NAME string literals)
// ============================================================================

type ShapeName = pptxgen.SHAPE_NAME;

function getShapeType(shape: string): ShapeName {
  switch (shape) {
    case 'rect': return 'rect';
    case 'ellipse': return 'ellipse';
    case 'arrow': return 'rightArrow';
    case 'line': return 'line';
    case 'roundedRect': return 'roundRect';
    default: return 'rect';
  }
}

// ============================================================================
// Chart Type Mapping (PptxGenJS v4 uses CHART_NAME string literals)
// ============================================================================

type ChartName = pptxgen.CHART_NAME;

function getChartType(chartType: string): ChartName {
  switch (chartType) {
    case 'bar': return 'bar';
    case 'line': return 'line';
    case 'pie': return 'pie';
    case 'doughnut': return 'doughnut';
    case 'scatter': return 'scatter';
    case 'bubble': return 'bubble';
    case 'radar': return 'radar';
    default: return 'bar';
  }
}

// ============================================================================
// Default Positions
// ============================================================================

const TITLE_POSITION = { x: 0.5, y: 0.5, w: 9, h: 1 };
const SUBTITLE_POSITION = { x: 0.5, y: 1.5, w: 9, h: 0.7 };
const CONTENT_POSITION = { x: 0.5, y: 1.8, w: 9, h: 3.5 };
const BODY_POSITION = { x: 0.5, y: 2.0, w: 9, h: 3.3 };

// ============================================================================
// Element Renderers
// ============================================================================

function renderTitle(slide: pptxgen.Slide, el: SlideElement & { type: 'title' }, isTitleSlide: boolean) {
  const opts: pptxgen.TextPropsOptions = {
    x: TITLE_POSITION.x,
    y: isTitleSlide ? 2 : TITLE_POSITION.y,
    w: TITLE_POSITION.w,
    h: TITLE_POSITION.h,
    fontSize: el.fontSize ?? (isTitleSlide ? 44 : 32),
    bold: true,
    align: el.align ?? (isTitleSlide ? 'center' : 'left'),
    color: cleanColor(el.color) || '1a1a1a',
    fontFace: el.fontFace,
  };
  slide.addText(el.text, opts);
}

function renderSubtitle(slide: pptxgen.Slide, el: SlideElement & { type: 'subtitle' }) {
  const opts: pptxgen.TextPropsOptions = {
    x: SUBTITLE_POSITION.x,
    y: SUBTITLE_POSITION.y,
    w: SUBTITLE_POSITION.w,
    h: SUBTITLE_POSITION.h,
    fontSize: el.fontSize ?? 24,
    color: cleanColor(el.color) || '666666',
    align: el.align ?? 'center',
    fontFace: el.fontFace,
  };
  slide.addText(el.text, opts);
}

function renderText(slide: pptxgen.Slide, el: TextElement) {
  const baseOpts: pptxgen.TextPropsOptions = {
    x: el.x ?? CONTENT_POSITION.x,
    y: el.y ?? CONTENT_POSITION.y,
    w: el.w ?? CONTENT_POSITION.w,
    h: el.h ?? 0.5,
    fontSize: el.fontSize ?? 18,
    bold: el.bold,
    italic: el.italic,
    underline: el.underline ? { style: 'sng' } : undefined,
    color: cleanColor(el.color) || '333333',
    align: el.align,
    valign: el.valign,
    fontFace: el.fontFace,
    charSpacing: el.charSpacing,
    margin: el.margin,
  };

  if (el.hyperlink?.url) {
    (baseOpts as Record<string, unknown>).hyperlink = { url: el.hyperlink.url };
  }

  // Rich text array
  if (Array.isArray(el.text)) {
    const textRuns: pptxgen.TextProps[] = (el.text as TextRun[]).map((run) => ({
      text: run.text,
      options: {
        bold: run.bold,
        italic: run.italic,
        underline: run.underline ? { style: 'sng' as const } : undefined,
        color: cleanColor(run.color),
        fontSize: run.fontSize,
        fontFace: run.fontFace,
        breakLine: run.breakLine,
        hyperlink: run.hyperlink?.url ? { url: run.hyperlink.url } : undefined,
      },
    }));
    slide.addText(textRuns, baseOpts);
  } else {
    slide.addText(el.text, baseOpts);
  }
}

function renderBullets(slide: pptxgen.Slide, el: BulletElement) {
  const items = el.items;
  const textRuns: pptxgen.TextProps[] = items.map((item, i) => {
    const isLast = i === items.length - 1;
    if (typeof item === 'string') {
      return {
        text: item,
        options: {
          bullet: true,
          breakLine: !isLast,
          fontSize: el.fontSize ?? 18,
          fontFace: el.fontFace,
          color: cleanColor(el.color) || '333333',
          paraSpaceAfter: el.paraSpaceAfter,
        },
      };
    }
    // BulletItem
    const bi = item as BulletItem;
    return {
      text: bi.text,
      options: {
        bullet: true,
        breakLine: !isLast,
        indentLevel: bi.indentLevel ?? 0,
        fontSize: bi.fontSize ?? el.fontSize ?? 18,
        fontFace: bi.fontFace ?? el.fontFace,
        color: cleanColor(bi.color) ?? cleanColor(el.color) ?? '333333',
        bold: bi.bold,
        italic: bi.italic,
        paraSpaceAfter: el.paraSpaceAfter,
      },
    };
  });

  slide.addText(textRuns, {
    x: el.x ?? BODY_POSITION.x,
    y: el.y ?? BODY_POSITION.y,
    w: el.w ?? BODY_POSITION.w,
    h: el.h ?? BODY_POSITION.h,
  });
}

function renderNumbered(slide: pptxgen.Slide, el: NumberedElement) {
  const items = el.items;
  const textRuns: pptxgen.TextProps[] = items.map((item, i) => {
    const isLast = i === items.length - 1;
    if (typeof item === 'string') {
      return {
        text: item,
        options: {
          bullet: { type: 'number' as const },
          breakLine: !isLast,
          fontSize: el.fontSize ?? 18,
          fontFace: el.fontFace,
          color: cleanColor(el.color) || '333333',
          paraSpaceAfter: el.paraSpaceAfter,
        },
      };
    }
    const bi = item as BulletItem;
    return {
      text: bi.text,
      options: {
        bullet: { type: 'number' as const },
        breakLine: !isLast,
        indentLevel: bi.indentLevel ?? 0,
        fontSize: bi.fontSize ?? el.fontSize ?? 18,
        fontFace: bi.fontFace ?? el.fontFace,
        color: cleanColor(bi.color) ?? cleanColor(el.color) ?? '333333',
        bold: bi.bold,
        italic: bi.italic,
        paraSpaceAfter: el.paraSpaceAfter,
      },
    };
  });

  slide.addText(textRuns, {
    x: el.x ?? BODY_POSITION.x,
    y: el.y ?? BODY_POSITION.y,
    w: el.w ?? BODY_POSITION.w,
    h: el.h ?? BODY_POSITION.h,
  });
}

function renderImage(slide: pptxgen.Slide, el: ImageElement) {
  const opts: pptxgen.ImageProps = {
    x: el.x ?? 2.5,
    y: el.y ?? 1.5,
    w: el.w ?? 5,
    h: el.h ?? 3,
    rounding: el.rounding,
    rotate: el.rotate,
    transparency: el.transparency,
    altText: el.altText,
  };

  // Determine source type
  if (el.src.startsWith('data:') || el.src.startsWith('image/')) {
    opts.data = el.src;
  } else {
    opts.path = el.src;
  }

  // Sizing mode
  if (el.sizing) {
    opts.sizing = {
      type: el.sizing.type,
      w: el.sizing.w ?? el.w ?? 5,
      h: el.sizing.h ?? el.h ?? 3,
      x: el.sizing.x,
      y: el.sizing.y,
    };
  }

  // Hyperlink
  if (el.hyperlink?.url) {
    opts.hyperlink = { url: el.hyperlink.url };
  }

  slide.addImage(opts);
}

function renderShape(slide: pptxgen.Slide, el: ShapeElement) {
  const shapeType = getShapeType(el.shape);

  const opts: pptxgen.ShapeProps = {
    x: el.x,
    y: el.y,
    w: el.w,
    h: el.h,
    fill: el.color ? { color: cleanColor(el.color)!, transparency: el.transparency } : undefined,
    rectRadius: el.rectRadius,
  };

  if (el.line) {
    opts.line = {
      color: cleanColor(el.line.color),
      width: el.line.width,
      dashType: el.line.dashType as pptxgen.ShapeLineProps['dashType'],
    };
  }

  // Always create fresh shadow object (PptxGenJS mutates in-place)
  if (el.shadow) {
    opts.shadow = buildShadow(el.shadow);
  }

  slide.addShape(shapeType, opts);
}

function renderTable(slide: pptxgen.Slide, el: TableElement) {
  // Convert our rows format to PptxGenJS table rows
  const tableRows: pptxgen.TableRow[] = el.rows.map((row) =>
    row.map((cell) => {
      if (typeof cell === 'string') {
        return { text: cell };
      }
      const tc = cell as PresentationTableCell;
      const cellOpts: pptxgen.TableCellProps = {};
      if (tc.options) {
        if (tc.options.bold !== undefined) cellOpts.bold = tc.options.bold;
        if (tc.options.italic !== undefined) cellOpts.italic = tc.options.italic;
        if (tc.options.color) cellOpts.color = cleanColor(tc.options.color);
        if (tc.options.fontSize) cellOpts.fontSize = tc.options.fontSize;
        if (tc.options.fontFace) cellOpts.fontFace = tc.options.fontFace;
        if (tc.options.fill) cellOpts.fill = { color: cleanColor(tc.options.fill.color)! };
        if (tc.options.align) cellOpts.align = tc.options.align;
        if (tc.options.valign) cellOpts.valign = tc.options.valign;
        if (tc.options.colspan) cellOpts.colspan = tc.options.colspan;
        if (tc.options.rowspan) cellOpts.rowspan = tc.options.rowspan;
        if (tc.options.border) {
          if (Array.isArray(tc.options.border)) {
            // PptxGenJS requires exactly [top, right, bottom, left] or a single object
            const mapped = tc.options.border.map(b => ({
              pt: b.pt ?? 1,
              color: cleanColor(b.color) || '999999',
            }));
            // Pad to 4 if less, using first item
            while (mapped.length < 4) mapped.push(mapped[0] || { pt: 1, color: '999999' });
            cellOpts.border = mapped.slice(0, 4) as [typeof mapped[0], typeof mapped[0], typeof mapped[0], typeof mapped[0]];
          } else {
            cellOpts.border = {
              pt: tc.options.border.pt ?? 1,
              color: cleanColor(tc.options.border.color) || '999999',
            };
          }
        }
      }
      return { text: tc.text, options: cellOpts };
    })
  );

  const tableOpts: pptxgen.TableProps = {
    x: el.x ?? 0.5,
    y: el.y ?? 1.5,
    w: el.w ?? 9,
    h: el.h,
    colW: el.colW,
    rowH: el.rowH,
    fontSize: el.fontSize,
    fontFace: el.fontFace,
    color: cleanColor(el.color),
    autoPage: el.autoPage,
  };

  if (el.border) {
    tableOpts.border = {
      pt: el.border.pt ?? 1,
      color: cleanColor(el.border.color) || '999999',
    };
  }

  if (el.fill) {
    tableOpts.fill = { color: cleanColor(el.fill.color)! };
  }

  slide.addTable(tableRows, tableOpts);
}

function renderChart(slide: pptxgen.Slide, el: ChartElement) {
  const chartType = getChartType(el.chartType);

  const chartData: pptxgen.OptsChartData[] = el.data.map((series) => ({
    name: series.name,
    labels: series.labels,
    values: series.values,
    sizes: series.sizes,
  }));

  const chartOpts: pptxgen.IChartOpts = {
    x: el.x ?? 0.5,
    y: el.y ?? 1,
    w: el.w ?? 9,
    h: el.h ?? 4,
    showTitle: el.showTitle,
    title: el.title,
    showLegend: el.showLegend,
    legendPos: el.legendPos,
    showValue: el.showValue,
    showPercent: el.showPercent,
    dataLabelPosition: el.dataLabelPosition === 'center' ? 'ctr' : el.dataLabelPosition as pptxgen.IChartOpts['dataLabelPosition'],
    dataLabelColor: cleanColor(el.dataLabelColor),
    chartColors: el.chartColors?.map(c => cleanColor(c)!),
  };

  // Bar-specific
  if (el.barDir) chartOpts.barDir = el.barDir;
  if (el.barGrouping) chartOpts.barGrouping = el.barGrouping;

  // Line-specific
  if (el.lineSize) chartOpts.lineSize = el.lineSize;
  if (el.lineSmooth !== undefined) chartOpts.lineSmooth = el.lineSmooth;

  // Chart area styling
  if (el.chartArea) {
    chartOpts.plotArea = {
      fill: el.chartArea.fill ? { color: cleanColor(el.chartArea.fill.color)! } : undefined,
    };
  }

  // Axis styling
  if (el.catAxisLabelColor) chartOpts.catAxisLabelColor = cleanColor(el.catAxisLabelColor);
  if (el.valAxisLabelColor) chartOpts.valAxisLabelColor = cleanColor(el.valAxisLabelColor);

  // Grid lines
  if (el.valGridLine) {
    chartOpts.valGridLine = {
      color: cleanColor(el.valGridLine.color),
      size: el.valGridLine.size,
      style: el.valGridLine.style as 'solid' | 'dash' | 'dot' | 'none' | undefined,
    };
  }
  if (el.catGridLine) {
    chartOpts.catGridLine = {
      color: cleanColor(el.catGridLine.color),
      size: el.catGridLine.size,
      style: el.catGridLine.style as 'solid' | 'dash' | 'dot' | 'none' | undefined,
    };
  }

  slide.addChart(chartType, chartData, chartOpts);
}

// ============================================================================
// Slide Rendering
// ============================================================================

function isTitleSlide(slide: SlideJSON): boolean {
  if (slide.layout === 'title') return true;
  const hasTitle = slide.elements.some(e => e.type === 'title');
  const hasSubtitle = slide.elements.some(e => e.type === 'subtitle');
  const hasBodyContent = slide.elements.some(e =>
    e.type === 'bullets' || e.type === 'numbered' || e.type === 'text'
  );
  return hasTitle && hasSubtitle && !hasBodyContent;
}

function renderElement(slide: pptxgen.Slide, element: SlideElement, isTitle: boolean) {
  switch (element.type) {
    case 'title':
      renderTitle(slide, element, isTitle && element.type === 'title');
      break;
    case 'subtitle':
      renderSubtitle(slide, element);
      break;
    case 'text':
      renderText(slide, element);
      break;
    case 'bullets':
      renderBullets(slide, element);
      break;
    case 'numbered':
      renderNumbered(slide, element);
      break;
    case 'image':
      renderImage(slide, element);
      break;
    case 'shape':
      renderShape(slide, element);
      break;
    case 'table':
      renderTable(slide, element);
      break;
    case 'chart':
      renderChart(slide, element);
      break;
  }
}

// ============================================================================
// Pending Binary Cache — avoids double buildPptxBlob() calls
// ============================================================================

const pendingBinaryMap = new Map<string, Promise<ArrayBuffer>>();

export function getPendingBinary(presentationId: string): Promise<ArrayBuffer> | undefined {
  return pendingBinaryMap.get(presentationId);
}

export function setPendingBinary(presentationId: string, promise: Promise<ArrayBuffer>): void {
  pendingBinaryMap.set(presentationId, promise);
  promise.finally(() => pendingBinaryMap.delete(presentationId));
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Build a PPTX Blob from PresentationJSON using PptxGenJS directly.
 */
export async function buildPptxBlob(pres: PresentationJSON): Promise<Blob> {
  const pptx = new pptxgen();

  // Presentation-level settings
  pptx.layout = pres.layout || 'LAYOUT_16x9';
  if (pres.author) pptx.author = pres.author;
  if (pres.title) pptx.title = pres.title;

  // Define slide masters
  if (pres.masters) {
    for (const master of pres.masters) {
      const masterDef: pptxgen.SlideMasterProps = {
        title: master.title,
      };
      if (master.background) {
        masterDef.background = {
          color: cleanColor(master.background.color),
          data: master.background.data,
          path: master.background.path,
        };
      }
      if (master.objects) {
        masterDef.objects = master.objects.map((obj) => {
          if (obj.placeholder) {
            return {
              placeholder: {
                options: {
                  name: obj.placeholder.options.name,
                  type: obj.placeholder.options.type as 'title' | 'body',
                  x: obj.placeholder.options.x,
                  y: obj.placeholder.options.y,
                  w: obj.placeholder.options.w,
                  h: obj.placeholder.options.h,
                },
              },
            };
          }
          if (obj.text) {
            return {
              text: {
                text: obj.text.text,
                options: {
                  x: obj.text.options.x,
                  y: obj.text.options.y,
                  w: obj.text.options.w,
                  h: obj.text.options.h,
                  fontSize: obj.text.options.fontSize,
                  color: cleanColor(obj.text.options.color),
                },
              },
            };
          }
          if (obj.image) {
            return {
              image: {
                data: obj.image.data,
                path: obj.image.path,
                x: obj.image.x,
                y: obj.image.y,
                w: obj.image.w,
                h: obj.image.h,
              },
            };
          }
          if (obj.shape) {
            return {
              rect: {
                x: obj.shape.x,
                y: obj.shape.y,
                w: obj.shape.w,
                h: obj.shape.h,
                fill: obj.shape.fill ? { color: cleanColor(obj.shape.fill.color)! } : undefined,
              },
            };
          }
          return {};
        }) as pptxgen.SlideMasterProps['objects'];
      }
      pptx.defineSlideMaster(masterDef);
    }
  }

  // Render slides
  for (const slideJson of pres.slides) {
    const slideOpts = slideJson.masterName
      ? { masterName: slideJson.masterName }
      : undefined;

    const slide = pptx.addSlide(slideOpts);

    // Background
    if (slideJson.backgroundImage) {
      if (slideJson.backgroundImage.startsWith('data:') || slideJson.backgroundImage.startsWith('image/')) {
        slide.background = { data: slideJson.backgroundImage };
      } else {
        slide.background = { path: slideJson.backgroundImage };
      }
    } else if (slideJson.backgroundColor) {
      slide.background = { color: cleanColor(slideJson.backgroundColor) };
    }

    // Speaker notes
    if (slideJson.notes) {
      slide.addNotes(slideJson.notes);
    }

    // Elements
    const isTitle = isTitleSlide(slideJson);
    for (const element of slideJson.elements) {
      renderElement(slide, element, isTitle);
    }
  }

  return await pptx.write({ outputType: 'blob' }) as Blob;
}

/**
 * Download the presentation as a .pptx file
 */
export async function downloadPptx(presentationJson: PresentationJSON, filename?: string, presentationId?: string): Promise<void> {
  let blob: Blob;

  if (presentationId) {
    const { loadPptxBinary } = await import('../stores/contentStore');
    const buffer = await loadPptxBinary(presentationId);
    if (buffer) {
      blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
    } else {
      blob = await buildPptxBlob(presentationJson);
    }
  } else {
    blob = await buildPptxBlob(presentationJson);
  }

  const finalFilename = filename || presentationJson.title || 'presentation';
  const sanitizedFilename = finalFilename.replace(/[^a-zA-Z0-9\s-_]/g, '').trim() || 'presentation';

  safeSaveAs(blob, `${sanitizedFilename}.pptx`);
}

/**
 * Get a summary of the presentation for preview display
 */
export function getPresentationSummary(presentationJson: PresentationJSON): {
  title: string;
  slideCount: number;
  hasImages: boolean;
} {
  try {
    let hasImages = false;

    for (const slide of presentationJson.slides || []) {
      for (const element of slide.elements || []) {
        if (element.type === 'image') {
          hasImages = true;
          break;
        }
      }
      if (hasImages) break;
    }

    return {
      title: presentationJson.title || 'Untitled Presentation',
      slideCount: (presentationJson.slides || []).length,
      hasImages,
    };
  } catch {
    return {
      title: presentationJson.title || 'Untitled Presentation',
      slideCount: 0,
      hasImages: false,
    };
  }
}
